import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { logger } from '../utils/logger';
import { globalToast } from '../utils/globalToast';
import { useAuth } from './AuthContext';
import { formatAddressParts, normalizeQuery } from '../utils/address';
import { db } from '../config/firebase';
import {
  collection,
  onSnapshot,
  setDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  limit,
  orderBy,
} from 'firebase/firestore';
import { COLLECTIONS } from '../config/constants';
import type { Customer, CustomerAddress } from '../shared/types';
export type { Customer, CustomerAddress };

// Undefined field temizleyici (tek seviye yeterli)
const stripUndefined = <T extends Record<string, unknown>>(obj: T): T => {
  if (!obj || typeof obj !== 'object') return obj;
  const clean = (Array.isArray(obj) ? [] : {}) as T;
  Object.keys(obj).forEach(k => {
    const v = obj[k as keyof T];
    if (v === undefined) return; // atla
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const nested = stripUndefined(v as Record<string, unknown>);
      (clean as Record<string, unknown>)[k] = nested;
    } else {
      (clean as Record<string, unknown>)[k] = v;
    }
  });
  return clean;
};

// Context tipi
interface CustomerContextType {
  customers: Customer[];
  addCustomer: (customer: Omit<Customer, 'id' | 'createdAt'>) => Customer;
  updateCustomer: (customer: Customer) => Promise<void>;
  deleteCustomer: (customerId: string) => void;
  // Firestore/local modu bile bile dışarı verilmiyor; UI tek işlemli kalır
  
  // Arama ve filtreleme
  searchCustomersByAddress: (addressQuery: string) => Customer[];
  getCustomerByFullAddress: (fullAddress: string) => Customer | undefined;
  getCustomerById: (id: string) => Customer | undefined;
  
  // Adres birleştirme
  getFullAddress: (customer: Customer) => string;
  
  // İstatistikler
  getCustomerCount: () => number;

  // Not yönetimi (sayfalar arası entegrasyon için)
  appendCustomerNote: (customerId: string, note: string) => void;
  setCustomerNotes: (customerId: string, notes: string[]) => Promise<void>;

  // Boş damacana sayacı (teslimat ekranı için): mevcut sayaca ekler
  addEmptyDamacana: (customerId: string, count: number) => Promise<void>;

  // Auth adresi ile müşteri adresini güncelle (sipariş sayfaları için)
  updateCustomerAddressFromAuth: (customerId: string, authAddress: any, phone?: string) => void;
}

// Context oluşturma
const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

// Hook
export const useCustomers = () => {
  const context = useContext(CustomerContext);
  if (context === undefined) {
    throw new Error('useCustomers must be used within a CustomerProvider');
  }
  return context;
};

// Provider props tipi
interface CustomerProviderProps {
  children: ReactNode;
}

// Yardımcılar
const STORAGE_KEY = 'customers-list';

const splitNotesHelper = (notes: string): string[] => (notes || '')
  .split('|')
  .map(n => n.trim())
  .filter(n => n.length > 0);
const sanitizeNotes = (notes: string): string => {
  // Notları sadece normalize et, filtreleme yapma
  const list = splitNotesHelper(notes);
  return list.join(' | ');
};
// Not listesini stringe çeviren yardımcı (saf — modül seviyesinde stabil)
const joinNotes = (list: string[]): string => list.join(' | ');

const sortByCreatedAtDesc = (list: Customer[]) =>
  [...list].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));

// Başlangıç verileri
const getInitialCustomers = (): Customer[] => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) {
    try {
      const parsed: Partial<Customer>[] = JSON.parse(saved);
      return sortByCreatedAtDesc(parsed.map(customer => ({
        id: customer.id || '',
        fullName: customer.fullName || '',
        phone: customer.phone || '',
        userUid: (customer as any).userUid || undefined,
        address: customer.address ? {
          neighborhood: customer.address.neighborhood || '',
          street: customer.address.street || '',
          buildingInfo: customer.address.buildingInfo || '',
          floor: (customer.address as any).floor || ''
        } : { neighborhood: '', street: '', buildingInfo: '', floor: '' },
        notes: sanitizeNotes(customer.notes || ''),
        tags: customer.tags || [],
        createdAt: customer.createdAt || new Date().toISOString(),
        lastOrderDate: customer.lastOrderDate,
        emptyDamacanaCount: typeof customer.emptyDamacanaCount === 'number' ? customer.emptyDamacanaCount : 0,
      })));
    } catch (error) {
      logger.error('Müşteri verileri yüklenirken hata:', error);
    }
  }
  
  // Başlangıç için örnek veriler
  return sortByCreatedAtDesc([
    {
      id: '1',
      fullName: 'Ahmet Yılmaz',
      phone: '0555 123 45 67',
      address: { neighborhood: 'Bostanlı', street: '1420. Sokak', buildingInfo: 'No: 12 D: 3' },
      notes: 'Siparişleri genellikle akşam saatlerinde verir.',
      tags: ['1420. Sokak', 'Özel Müşteri'],
      createdAt: new Date().toISOString(),
    },
    {
      id: '2',
      fullName: 'Zeynep Kaya',
      phone: '0544 987 65 43',
      address: { neighborhood: 'Göztepe', street: '1421. Sokak', buildingInfo: 'Apatman Adı, Kat: 5' },
      notes: 'Nakit ödeme yapmayı tercih ediyor. Eski Borç: 200 ₺',
      tags: ['1421. Sokak'],
      createdAt: new Date().toISOString(),
    },
    {
      id: '3',
      fullName: 'Mustafa Demir',
      phone: '0533 111 22 33',
      address: { neighborhood: 'Alsancak', street: '1420. Sokak', buildingInfo: 'İş Hanı No: 45' },
      notes: '',
      tags: ['1420. Sokak', 'İş Yeri'],
      createdAt: new Date().toISOString(),
    },
    {
      id: '4',
      fullName: 'Fatma Şahin',
      phone: '0555 444 33 22',
      address: { neighborhood: 'Karşıyaka', street: '1421. Sokak', buildingInfo: 'No: 78 K: 2 D: 8' },
      notes: 'Boş damacana istenecek. Kapıcıya bırakılabilir.',
      tags: ['1421. Sokak', 'Apartman'],
      createdAt: new Date().toISOString(),
    },
  ]);
};

// Provider bileşeni
export const CustomerProvider: React.FC<CustomerProviderProps> = ({ children }) => {
  const USE_FB_CUSTOMERS = (process.env.REACT_APP_USE_FIREBASE_CUSTOMERS || 'true').toLowerCase() === 'true';
  const [customers, setCustomers] = useState<Customer[]>(() => USE_FB_CUSTOMERS ? [] : getInitialCustomers());
  const { currentUser, loading, getAddresses, getLastUsedAddress } = useAuth();
  const isAdmin = useMemo(() => (currentUser?.role || '').toLowerCase() === 'admin', [currentUser?.role]);

  const applyLocalUpdate = useCallback((updater: (prev: Customer[]) => Customer[], opts?: { force?: boolean }) => {
    if (USE_FB_CUSTOMERS && !opts?.force) return;
    setCustomers(prev => {
      const next = updater(prev);
      if (!USE_FB_CUSTOMERS || opts?.force) {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
      }
      return next;
    });
  }, [USE_FB_CUSTOMERS]);

  // Firestore realtime listener
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!USE_FB_CUSTOMERS) {
      return;
    }
    // Auth kontrolü
    if (loading) return;
    if (!currentUser) {
      setCustomers([]);
      return;
    }

    const colRef = collection(db, COLLECTIONS.CUSTOMERS);
    
    // PERFORMANS OPTİMİZASYONU:
    // Faz C-Acil: Initial RAM ve TTI için limit düşürüldü.
    // Cursor pagination ve sunucu taraflı arama Faz C-Type'ta eklenecek.
    let colQuery;

    if (isAdmin) {
       colQuery = query(colRef, orderBy('createdAt', 'desc'), limit(200));
    } else {
       colQuery = query(colRef, where('userUid', '==', currentUser.uid));
    }

    const unsub = onSnapshot(
      colQuery,
      (snap) => {
        const list: Customer[] = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            fullName: data.fullName || '',
            phone: data.phone || '',
            userUid: data.userUid || undefined,
            address: data.address || { neighborhood: '', street: '', buildingInfo: '', floor: '' },
            notes: sanitizeNotes(data.notes || ''),
            tags: Array.isArray(data.tags) ? data.tags : (data.tags ? [data.tags] : []),
            createdAt: typeof data.createdAt === 'string'
              ? data.createdAt
              : new Date((data.createdAt?.seconds * 1000) || Date.now()).toISOString(),
            lastOrderDate: data.lastOrderDate,
            emptyDamacanaCount: typeof data.emptyDamacanaCount === 'number' ? data.emptyDamacanaCount : 0,
          } as Customer;
        });
  setCustomers(sortByCreatedAtDesc(list));
      },
      (error) => {
        logger.error('Firestore customers dinleme hatası:', error);
        setCustomers([]);
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [USE_FB_CUSTOMERS, currentUser?.uid, isAdmin, loading]);

  // Müşteri ekleme
  const addCustomer = useCallback((customerData: Omit<Customer, 'id' | 'createdAt'>): Customer => {
    const resolvedUserUid = customerData.userUid || undefined;
    const newCustomer: Customer = {
      ...customerData,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      userUid: resolvedUserUid,
    };
    
    if (USE_FB_CUSTOMERS) {
      const payload = stripUndefined({
        ...newCustomer,
        userUid: newCustomer.userUid || null
      });

      setDoc(doc(db, COLLECTIONS.CUSTOMERS, newCustomer.id), payload).catch((e: any) => {
        const errorCode = e?.code || 'unknown';
        const errorMsg = e?.message || 'Bilinmeyen hata';
        logger.error('Müşteri ekleme hatası:', {
          code: errorCode,
          message: errorMsg,
          customerData: { fullName: newCustomer.fullName, phone: newCustomer.phone }
        });
        
        // Kullanıcı dostu hata mesajı
        let userMessage = 'Müşteri kaydedilirken bir sorun oluştu.';
        
        if (errorCode === 'permission-denied') {
          userMessage = 'Müşteri eklemek için yetkiniz bulunmuyor.';
        } else if (errorCode.includes('network') || errorCode === 'unavailable') {
          userMessage = 'İnternet bağlantınızı kontrol edin ve tekrar deneyin.';
        }
        
        globalToast.error(userMessage);
      });
    } else {
  applyLocalUpdate(prev => sortByCreatedAtDesc([newCustomer, ...prev]));
    }

    return newCustomer;
  }, [USE_FB_CUSTOMERS, applyLocalUpdate]);

  // Müşteri güncelleme
  const updateCustomer = useCallback(async (updatedCustomer: Customer): Promise<void> => {
    if (USE_FB_CUSTOMERS) {
      const cleanCustomerData = stripUndefined({
        ...updatedCustomer,
        userUid: updatedCustomer.userUid ?? null,
        lastOrderDate: updatedCustomer.lastOrderDate || null,
        notes: updatedCustomer.notes || '',
        tags: updatedCustomer.tags || [],
        createdAt: updatedCustomer.createdAt || new Date().toISOString()
      });

      try {
        await updateDoc(doc(db, COLLECTIONS.CUSTOMERS, updatedCustomer.id), cleanCustomerData);
      } catch (e: any) {
        const errorCode = e?.code || 'unknown';
        const errorMsg = e?.message || 'Bilinmeyen hata';
        logger.error('Müşteri güncelleme hatası:', {
          code: errorCode,
          message: errorMsg,
          customerId: updatedCustomer.id,
          customerName: updatedCustomer.fullName
        });
        
        // Kullanıcı dostu hata mesajı
        let userMessage = 'Müşteri bilgileri güncellenirken bir sorun oluştu.';
        
        if (errorCode === 'permission-denied') {
          userMessage = 'Bu işlem için yetkiniz bulunmuyor. Lütfen yönetici ile iletişime geçin.';
        } else if (errorCode === 'not-found') {
          userMessage = 'Müşteri kaydı bulunamadı. Sayfa yenilenecek.';
          setTimeout(() => window.location.reload(), 2000);
        } else if (errorCode.includes('network') || errorCode === 'unavailable') {
          userMessage = 'İnternet bağlantınızı kontrol edin ve tekrar deneyin.';
        }
        
        globalToast.error(userMessage);
        throw e; // Hata üst katmana da iletilsin
      }
    } else {
      applyLocalUpdate(prev => prev.map(c => c.id === updatedCustomer.id ? { ...updatedCustomer } : c));
    }
  }, [USE_FB_CUSTOMERS, applyLocalUpdate]);

  // Müşteri silme
  const deleteCustomer = useCallback((customerId: string) => {
    if (USE_FB_CUSTOMERS) {
      deleteDoc(doc(db, COLLECTIONS.CUSTOMERS, customerId)).catch((e: any) => {
        logger.error('Müşteri silme hatası:', e);
        globalToast.error('Müşteri silinirken hata oluştu.');
      });
    } else {
      applyLocalUpdate(prev => prev.filter(c => c.id !== customerId));
    }
  }, [USE_FB_CUSTOMERS, applyLocalUpdate]);

  // Auth sonrası kullanıcı için müşteri kaydı oluştur/gerçekleştir (upsert)
  // Not: Sadece admin tarafından manuel olarak müşteri kayıtları oluşturulur
  // Müşteri bilgileri users koleksiyonunda tutulur, customers koleksiyonu sadece admin yönetimi içindir
  useEffect(() => {
    const handler = (e: any) => {
      const { uid, fullName, email } = (e?.detail || {}) as { uid: string; fullName: string; email: string };
      if (!uid) return;
      
      // Sadece mevcut kayıtları güncelle, yeni kayıt oluşturma
      const norm = (s: string) => normalizeQuery(s || '');
      let found = customers.find(c => c.userUid === uid) || customers.find(c => c.id === uid);
      
      if (!found) {
        found = customers.find(c => norm(c.fullName) === norm(fullName || (email ? email.split('@')[0] : '')));
      }

      // Sadece eşleşme varsa güncelle (admin tarafından önceden oluşturulmuşsa)
      if (found && isAdmin) {
        const updated: Customer = {
          ...found,
          userUid: uid,
          fullName: found.fullName || (fullName || ''),
          phone: found.phone || ''
        };

        if (USE_FB_CUSTOMERS) {
          updateDoc(doc(db, COLLECTIONS.CUSTOMERS, updated.id), {
            userUid: uid,
            fullName: updated.fullName,
            phone: updated.phone
          }).catch(() => {});
        } else {
          applyLocalUpdate(prev => prev.map(c => c.id === updated.id ? updated : c));
        }
      }
      // Yeni kayıt oluşturma - sadece admin manuel olarak yapabilir
    };
    window.addEventListener('ensureCustomerProfile', handler);
    return () => window.removeEventListener('ensureCustomerProfile', handler);
  }, [customers, USE_FB_CUSTOMERS, applyLocalUpdate, isAdmin]);

  // Kullanıcı hesabındaki (AuthContext) varsayılan/son kullanılan adresi
  // müşteriler koleksiyonuna senkronla ki yönetim ekranında görünsün
  useEffect(() => {
    const pickProfileAddress = () => {
      try {
        const list = getAddresses ? getAddresses() : [];
        if (!Array.isArray(list) || list.length === 0) return null;
        const prefId = (getLastUsedAddress && getLastUsedAddress()) || '';
        const chosen = list.find(a => a.id === prefId) || list.find(a => a.isDefault) || list[0];
        return chosen || null;
      } catch {
        return null;
      }
    };

    const syncFromAuthProfile = () => {
      const uid = currentUser?.uid;
      if (!uid) return;
      const addr = pickProfileAddress();
      if (!addr) return;

      // Haritalama: Auth.Address -> Customer.address
      const mapped = {
        neighborhood: (addr as any).district || '',
        street: (addr as any).address || '',
        buildingInfo: [ (addr as any).city || '', (addr as any).postalCode || '' ].filter(Boolean).join(' ').trim(),
        floor: ''
      } as Customer['address'];

      const existing = customers.find(c => c.id === uid);
      if (!existing) return;

      const oldAddr = formatAddressParts(existing.address || { neighborhood: '', street: '', buildingInfo: '' });
      const newAddr = formatAddressParts(mapped);
      const phoneNext = existing.phone || (addr as any).phone || '';

      // Değişiklik yoksa yazma
      if (normalizeQuery(oldAddr) === normalizeQuery(newAddr) && (existing.phone || '') === (phoneNext || '')) return;

      updateCustomer({ ...existing, address: mapped, phone: phoneNext });
    };

    // İlk yüklemede ve profil değiştiğinde çalıştır
    syncFromAuthProfile();
    const onProfileChanged = (e: any) => {
      const uid = e?.detail?.uid;
      if (!uid || uid !== currentUser?.uid) return;
      syncFromAuthProfile();
    };
    window.addEventListener('authProfileChanged', onProfileChanged);
    return () => window.removeEventListener('authProfileChanged', onProfileChanged);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, customers]);

  // Adres birleştirme fonksiyonu (tek merkezi util)
  const getFullAddress = useCallback((customer: Customer): string => {
    return formatAddressParts(customer.address);
  }, []);

  // Pre-computed normalized search index - customers değiştiğinde bir kez hesaplanır
  const customerSearchIndex = useMemo(() => {
    return customers.map(customer => {
      const fullAddress = formatAddressParts(customer.address);
      const { neighborhood, street, buildingInfo, floor } = customer.address;
      return {
        customer,
        normalizedFullAddress: normalizeQuery(fullAddress),
        normalizedStreet: normalizeQuery(street || ''),
        normalizedFields: [neighborhood, street, buildingInfo, floor || '', customer.fullName || '']
          .map(v => normalizeQuery(v)),
        fullAddress
      };
    });
  }, [customers]);

  // Adres bazlı arama - memoized with pre-computed index
  const searchCustomersByAddress = useCallback((addressQuery: string): Customer[] => {
    if (!addressQuery.trim()) return [];

    const queryNormalized = normalizeQuery(addressQuery);

    return customerSearchIndex
      .filter(indexed => {
        return (
          indexed.normalizedFullAddress.includes(queryNormalized) ||
          indexed.normalizedFields.some(f => f.includes(queryNormalized))
        );
      })
      .sort((a, b) => {
        // Sokak adı ile başlayanları önce göster
        if (a.normalizedStreet.startsWith(queryNormalized) && !b.normalizedStreet.startsWith(queryNormalized)) return -1;
        if (!a.normalizedStreet.startsWith(queryNormalized) && b.normalizedStreet.startsWith(queryNormalized)) return 1;

        // Son sipariş tarihine göre sırala (varsa)
        const aDate = a.customer.lastOrderDate;
        const bDate = b.customer.lastOrderDate;
        if (aDate && bDate) {
          return new Date(bDate).getTime() - new Date(aDate).getTime();
        }
        if (aDate && !bDate) return -1;
        if (!aDate && bDate) return 1;

        // Alfabetik sıralama
        return a.fullAddress.localeCompare(b.fullAddress, 'tr');
      })
      .map(indexed => indexed.customer);
  }, [customerSearchIndex]);

  // Tam adrese göre müşteri bulma - pre-computed index kullanır
  const getCustomerByFullAddress = useCallback((fullAddress: string): Customer | undefined => {
    const target = normalizeQuery(fullAddress || '');
    if (!target) return undefined;

    // 1. Önce tam eşleşme dene (pre-computed index kullan)
    const exactMatch = customerSearchIndex.find(indexed => indexed.normalizedFullAddress === target);
    if (exactMatch) return exactMatch.customer;

    // 2. Tam eşleşme yoksa, en iyi kısmi eşleşmeyi bul
    const partialMatches = customerSearchIndex.filter(indexed => {
      return indexed.normalizedFullAddress.includes(target) || target.includes(indexed.normalizedFullAddress);
    });

    if (partialMatches.length === 1) return partialMatches[0].customer;

    // 3. Birden fazla kısmi eşleşme varsa, en uzun eşleşeni seç
    if (partialMatches.length > 1) {
      return partialMatches.sort((a, b) => {
        const diffA = Math.abs(a.normalizedFullAddress.length - target.length);
        const diffB = Math.abs(b.normalizedFullAddress.length - target.length);
        return diffA - diffB;
      })[0].customer;
    }

    return undefined;
  }, [customerSearchIndex]);

  // Id'ye göre müşteri bulma - memoized Map for O(1) lookup
  const customerIdMap = useMemo(() => {
    const map = new Map<string, Customer>();
    customers.forEach(c => map.set(c.id, c));
    return map;
  }, [customers]);

  const getCustomerById = useCallback((id: string): Customer | undefined => {
    return customerIdMap.get(id);
  }, [customerIdMap]);

  // Müşteriye not ekle (tekrarları engeller)
  const appendCustomerNote = useCallback((customerId: string, note: string) => {
    const customer = getCustomerById(customerId);
    if (!customer) return;
    const list = splitNotesHelper(customer.notes || '');
    // Not zaten listede yoksa ekle
    if (!list.includes(note.trim())) {
      list.push(note.trim());
    }
    // Tüm notları birleştir (filtre uygulamadan)
    updateCustomer({ ...customer, notes: joinNotes(list) });
  }, [getCustomerById, updateCustomer]);

  // Müşteri notlarını komple ayarla (varolanların yerini alır)
  const setCustomerNotes = useCallback(async (customerId: string, notes: string[]): Promise<void> => {
    const customer = getCustomerById(customerId);
    if (!customer) return;
    // Tüm notları birleştir (filtre uygulamadan)
    await updateCustomer({ ...customer, notes: joinNotes(notes) });
  }, [getCustomerById, updateCustomer]);

  // Boş damacana sayacına ekle (teslimat ekranı için)
  const addEmptyDamacana = useCallback(async (customerId: string, count: number): Promise<void> => {
    if (!customerId || !count || count <= 0) return;
    const customer = getCustomerById(customerId);
    if (!customer) return;
    const next = (customer.emptyDamacanaCount || 0) + count;
    await updateCustomer({ ...customer, emptyDamacanaCount: next });
  }, [getCustomerById, updateCustomer]);

  // Auth adresi ile müşteri adresini güncelle (sipariş sayfaları için)
  // Window event yerine doğrudan fonksiyon çağrısı
  const updateCustomerAddressFromAuth = useCallback((customerId: string, authAddress: any, phone?: string) => {
    if (!customerId || !authAddress) return;

    // Müşteriyi bul (userUid, id veya fullName ile)
    const existing = customers.find(c => c.userUid === customerId)
      || customers.find(c => c.id === customerId)
      || customers.find(c => normalizeQuery(c.fullName) === normalizeQuery((authAddress?.firstName || '') + ' ' + (authAddress?.lastName || '')));

    if (!existing) return;

    // Auth.Address -> Customer.address eşleme
    const mapped = {
      neighborhood: (authAddress as any).district || '',
      street: (authAddress as any).address || '',
      buildingInfo: [(authAddress as any).city || '', (authAddress as any).postalCode || ''].filter(Boolean).join(' ').trim(),
      floor: ''
    } as Customer['address'];

    const nextPhone = phone || existing.phone || '';
    const oldAddr = formatAddressParts(existing.address || { neighborhood: '', street: '', buildingInfo: '' });
    const newAddr = formatAddressParts(mapped);

    // Değişiklik yoksa güncelleme yapma
    if (normalizeQuery(oldAddr) === normalizeQuery(newAddr) && (existing.phone || '') === (nextPhone || '')) return;

    updateCustomer({ ...existing, address: mapped, phone: nextPhone });
  }, [customers, updateCustomer]);

  // İstatistikler
  const getCustomerCount = useCallback(() => customers.length, [customers.length]);

  // Context value memoization - gereksiz re-render'ları önler
  const contextValue = useMemo<CustomerContextType>(() => ({
    customers,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    searchCustomersByAddress,
    getCustomerByFullAddress,
    getCustomerById,
    getFullAddress,
    getCustomerCount,
    appendCustomerNote,
    setCustomerNotes,
    addEmptyDamacana,
    updateCustomerAddressFromAuth,
  }), [
    customers,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    searchCustomersByAddress,
    getCustomerByFullAddress,
    getCustomerById,
    getFullAddress,
    getCustomerCount,
    appendCustomerNote,
    setCustomerNotes,
    addEmptyDamacana,
    updateCustomerAddressFromAuth,
  ]);

  return (
    <CustomerContext.Provider value={contextValue}>
      {children}
    </CustomerContext.Provider>
  );
};

export default CustomerContext;

